import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, CheckCircle2, Users, ArrowRight, Loader2 } from "lucide-react";

const INSTAGRAM_URL = "https://www.instagram.com/ikizlermarketlefke";

export default function InstagramDiscountDialog({ open, onOpenChange, profile }) {
  const { t, settings } = useApp();
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const igUrl =
    settings?.instagram_url && settings.instagram_url.startsWith("http")
      ? settings.instagram_url
      : INSTAGRAM_URL;

  const openInstagram = () => {
    window.open(igUrl, "_blank", "noopener");
    setStep(2);
  };

  const openLatestPost = () => {
    window.open(igUrl, "_blank", "noopener");
    setStep(3);
  };

  const submit = async () => {
    if (!profile?.customer_number) {
      toast.error(t("ig_need_customer"));
      return;
    }
    const uname = username.trim().replace(/^@/, "");
    if (!uname) {
      toast.error(t("ig_username_required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/discount-requests", {
        customer_number: profile.customer_number,
        phone: profile.phone,
        instagram_username: uname,
      });
      toast.success(t("ig_request_success"));
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("ig_request_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setStep(1);
    setUsername("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="ig-discount-dialog"
        className="bg-[#FAF7F2] border-[#E8E2D5] max-w-md max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
              <Instagram className="w-5 h-5 text-white" />
            </div>
            {t("ig_discount_title")}
          </DialogTitle>
          <DialogDescription>{t("ig_discount_desc")}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between mt-1 mb-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 flex items-center">
              <div
                className={`w-8 h-8 rounded-full font-heading font-bold flex items-center justify-center border-2 transition ${
                  step >= s
                    ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]"
                    : "bg-white text-[#1B2A22]/40 border-[#E8E2D5]"
                }`}
              >
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 3 && (
                <div
                  className={`flex-1 h-0.5 mx-1 ${
                    step > s ? "bg-[#2E5A36]" : "bg-[#E8E2D5]"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div className="bg-white border border-[#E8E2D5] rounded-xl p-4">
              <p className="text-xs uppercase font-heading font-bold text-[#2E5A36]">
                {t("ig_step1_label")}
              </p>
              <p className="text-sm text-[#1B2A22]/80 mt-1">{t("ig_step1_desc")}</p>
            </div>
            <Button
              data-testid="ig-step1-btn"
              onClick={openInstagram}
              className="w-full h-12 bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white rounded-full font-heading font-bold"
            >
              <Instagram className="w-5 h-5 mr-2" />
              {t("ig_step1_action")}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="bg-white border border-[#E8E2D5] rounded-xl p-4">
              <p className="text-xs uppercase font-heading font-bold text-[#2E5A36]">
                {t("ig_step2_label")}
              </p>
              <p className="text-sm text-[#1B2A22]/80 mt-1">{t("ig_step2_desc")}</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 rounded-full"
              >
                {t("back")}
              </Button>
              <Button
                data-testid="ig-step2-btn"
                onClick={openLatestPost}
                className="flex-1 bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full"
              >
                <Users className="w-4 h-4 mr-2" />
                {t("ig_step2_action")}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="bg-white border border-[#E8E2D5] rounded-xl p-4">
              <p className="text-xs uppercase font-heading font-bold text-[#2E5A36]">
                {t("ig_step3_label")}
              </p>
              <p className="text-sm text-[#1B2A22]/80 mt-1">{t("ig_step3_desc")}</p>
            </div>
            <div>
              <Label>{t("ig_username_label")}</Label>
              <Input
                data-testid="ig-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="@kullanici_adiniz"
                className="bg-white"
              />
              <p className="text-[10px] text-[#1B2A22]/60 mt-1">
                {t("ig_username_hint")}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 rounded-full"
              >
                {t("back")}
              </Button>
              <Button
                data-testid="ig-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="flex-1 bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {t("ig_submit_btn")} <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="text-center py-3 space-y-3">
            <div className="w-16 h-16 rounded-full bg-[#2E5A36] flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-[#FAF7F2]" />
            </div>
            <h3 className="font-heading font-extrabold text-xl text-[#1B2A22]">
              {t("ig_success_title")}
            </h3>
            <p className="text-sm text-[#1B2A22]/70">{t("ig_success_desc")}</p>
            <div className="bg-[#E5A024]/10 border border-[#E5A024]/40 rounded-xl p-3 text-xs text-[#B4711C] text-left">
              💡 {t("ig_success_note")}
            </div>
            <Button
              data-testid="ig-close-btn"
              onClick={close}
              className="w-full bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full"
            >
              {t("close")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
